import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ReferralRecord } from '../modules/referrals/referralPageTypes';
import { useAppContext } from '../utils/hooks/useAppContext';
import { useDirectorPracticeView } from '../utils/hooks/useDirectorPracticeView';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface GetReferralsResponse {
  referrals: ReferralRecord[];
  pagination: PaginationInfo;
  filters: {
    appliedFilters: {
      status?: string;
      specialty_id?: number;
      practice_id?: string;
      date_range?: string;
      search?: string;
    };
    availableFilters: {
      statuses: Array<{ value: string; label: string; count: number }>;
      specialties: Array<{ id: number; name: string; count: number }>;
      practices: Array<{ id: string; name: string; count: number }>;
    };
  };
}

export interface GetReferralsQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  specialty_id?: number;
  practice_id?: string;
  destination_practice_id?: string;
  date_range?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  sort_by?: 'created_at' | 'updated_at' | 'patient_name' | 'status';
  sort_order?: 'asc' | 'desc';
}

export interface ReferralMetrics {
  submitted: number;
  notStarted: number;
  needsReferral: number;
  failed: number;
  inProgress: number;
  completed: number;
  total: number;
}

export interface MetricTrend {
  current: number;
  previous: number;
  change: number;
}

export interface GetReferralsMetricsResponse {
  metrics: ReferralMetrics;
  dateRange: {
    start: string;
    end: string;
    label: string;
  };
  trends: {
    submitted: MetricTrend;
    notStarted: MetricTrend;
    needsReferral: MetricTrend;
    failed: MetricTrend;
    inProgress: MetricTrend;
    completed: MetricTrend;
  };
}

export interface GetReferralsMetricsParams {
  practice_id?: string;
  destination_practice_id?: string;
  specialty_id?: number;
  date_range?: string;
  start_date?: string;
  end_date?: string;
}

export interface GetReferralsFiltersResponse {
  referrals_practices: FilterOption[];
  specialties: SpecialtyFilterOption[];
  procedures: ProcedureFilterOption[];
  statuses: StatusFilterOption[];
  dateRanges: DateRangeFilterOption[];
}

export interface FilterOption {
  id: string;
  name: string;
  count: number;
}

export interface SpecialtyFilterOption {
  id: number;
  name: string;
  count: number;
}

export interface ProcedureFilterOption {
  id: number;
  name: string;
  specialtyId: number;
  count: number;
}

export interface StatusFilterOption {
  value: string;
  label: string;
  count: number;
}

export interface DateRangeFilterOption {
  value: string;
  label: string;
}

export interface GetReferralsFiltersParams {
  practice_id?: string;
}

// Helper function to get the effective practice ID for referrals queries
// This handles both regular users and director practice view mode
const useEffectivePracticeId = () => {
  const { user } = useAppContext();
  const { directorViewPracticeId, isDirectorPracticeViewUser } =
    useDirectorPracticeView();

  // If in director practice view mode, use the director's selected practice
  // Otherwise, use the user's primary practice
  return isDirectorPracticeViewUser
    ? directorViewPracticeId
    : user?.primaryPracticeId;
};

// Hook for paginated referrals list (offset-based pagination)
export const useReferrals = (
  params?: GetReferralsQueryParams,
  options?: Omit<
    UseQueryOptions<
      GetReferralsResponse,
      unknown,
      GetReferralsResponse,
      [
        'referrals',
        'list',
        GetReferralsQueryParams | undefined,
        string | undefined,
      ]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { user } = useAppContext();
  const effectivePracticeId = useEffectivePracticeId();

  return useQuery<
    GetReferralsResponse,
    unknown,
    GetReferralsResponse,
    [
      'referrals',
      'list',
      GetReferralsQueryParams | undefined,
      string | undefined,
    ]
  >(
    ['referrals', 'list', params, effectivePracticeId],
    () =>
      makeAPIRequestReactQuery<GetReferralsResponse>(
        endpoints.referrals,
        {
          ...params,
          practice_id: params?.practice_id || effectivePracticeId,
        },
        'get',
        {
          headers: {
            Org: user?.primaryOrganizationId,
          },
        },
      ),
    {
      enabled: !!user?.primaryOrganizationId && !!effectivePracticeId,
      staleTime: 1000 * 60 * 2, // 2 minutes
      ...options,
    },
  );
};

// Hook for referrals metrics
export const useReferralsMetrics = (
  params?: GetReferralsMetricsParams,
  options?: Omit<
    UseQueryOptions<
      GetReferralsMetricsResponse,
      unknown,
      GetReferralsMetricsResponse,
      [
        'referrals',
        'metrics',
        GetReferralsMetricsParams | undefined,
        string | undefined,
      ]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { user } = useAppContext();
  const effectivePracticeId = useEffectivePracticeId();

  return useQuery<
    GetReferralsMetricsResponse,
    unknown,
    GetReferralsMetricsResponse,
    [
      'referrals',
      'metrics',
      GetReferralsMetricsParams | undefined,
      string | undefined,
    ]
  >(
    ['referrals', 'metrics', params, effectivePracticeId],
    () =>
      makeAPIRequestReactQuery<GetReferralsMetricsResponse>(
        endpoints.referralsMetrics,
        {
          ...params,
          practice_id: params?.practice_id || effectivePracticeId,
        },
        'get',
        {
          headers: {
            Org: user?.primaryOrganizationId,
          },
        },
      ),
    {
      enabled: !!user?.primaryOrganizationId && !!effectivePracticeId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      ...options,
    },
  );
};

// Hook for referrals filter options
export const useReferralsFilters = (
  params?: GetReferralsFiltersParams,
  options?: Omit<
    UseQueryOptions<
      GetReferralsFiltersResponse,
      unknown,
      GetReferralsFiltersResponse,
      [
        'referrals',
        'filters',
        GetReferralsFiltersParams | undefined,
        string | undefined,
      ]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const { user } = useAppContext();
  const effectivePracticeId = useEffectivePracticeId();

  return useQuery<
    GetReferralsFiltersResponse,
    unknown,
    GetReferralsFiltersResponse,
    [
      'referrals',
      'filters',
      GetReferralsFiltersParams | undefined,
      string | undefined,
    ]
  >(
    ['referrals', 'filters', params, effectivePracticeId],
    () =>
      makeAPIRequestReactQuery<GetReferralsFiltersResponse>(
        endpoints.referralsFilters,
        {
          ...params,
          practice_id: params?.practice_id || effectivePracticeId,
        },
        'get',
        {
          headers: {
            Org: user?.primaryOrganizationId,
          },
        },
      ),
    {
      enabled: !!user?.primaryOrganizationId && !!effectivePracticeId,
      staleTime: 1000 * 60 * 60, // 1 hour (filters rarely change)
      ...options,
    },
  );
};
